import { UserGroupIcon } from "@heroicons/react/20/solid";
import type { DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ListedGroup } from "../../../api/access/accessModel";
import Chip from "../../../ui/feedback/Chip";
import PinIcon from "../../../ui/icons/PinIcon";
import type { MenuSection } from "../../../ui/overlays/MenuButton";
import { classNames } from "../../../ui/styles/classNames";
import { isPinned } from "../model/groupOrder";
import GrantChips from "./GrantChips";
import GroupMenu from "./GroupMenu";
import GroupSize from "./GroupSize";

interface GroupListRowProps {
  group: ListedGroup;
  projectId: string | null;
  itemKinds: readonly string[];
  name: string;
  parentName: string | null;
  depth: number;
  href: string;
  busy: boolean;
  /** A dragged group would land above this one. */
  dropMarked: boolean;
  /** The group's menu (`groupMenuSections`); none shows no menu. */
  menuSections: readonly MenuSection[];
  /** Set for a group the viewer may move. */
  drag: { readonly type: string; readonly onStart: () => void; readonly onEnd: () => void } | undefined;
  onDragOver: (event: DragEvent<HTMLLIElement>) => void;
  onDrop: (event: DragEvent<HTMLLIElement>) => void;
}

/** One group in its list: its place, name, size and what it allows, and its menu. */
export default function GroupListRow({ group, projectId, itemKinds, name, parentName, depth, href, busy, dropMarked, menuSections, drag, onDragOver, onDrop }: GroupListRowProps) {
  const { t } = useTranslation();
  const included = projectId !== null && group.projectId === null;
  return (
    <li
      className={classNames("flex items-center gap-3 px-4 py-2.5", dropMarked && "border-t-2 border-t-[var(--app-accent)]")}
      style={{ paddingInlineStart: `${1 + depth * 1.5}rem` }}
      draggable={drag !== undefined && !busy}
      onDragStart={drag === undefined ? undefined : (event) => {
        event.dataTransfer.setData(drag.type, group.groupId);
        event.dataTransfer.effectAllowed = "move";
        drag.onStart();
      }}
      onDragEnd={drag?.onEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <span className="app-text-muted w-4 shrink-0" aria-hidden="true">
        {isPinned(group) ? <PinIcon className="h-4 w-4" filled={true} /> : drag === undefined ? null : <span className="cursor-grab select-none">⋮</span>}
      </span>
      <span className={classNames("flex h-7 w-7 shrink-0 items-center justify-center rounded", group.projectId === null ? "bg-[var(--app-warning-soft)]" : "bg-[var(--app-surface-muted)]")} aria-hidden="true">
        <UserGroupIcon className="h-4 w-4" />
      </span>
      {/* The name and its size on one line ("Contributors · 2 people"); what the group allows beneath. */}
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <Link to={href} className="font-semibold hover:underline">{name}</Link>
          {included ? <Chip>{t("access.installationBadge")}</Chip> : null}
          <span className="app-text-muted whitespace-nowrap text-xs"><span aria-hidden="true">· </span><GroupSize group={group} /></span>
        </p>
        <GrantChips group={group} projectId={projectId} itemKinds={itemKinds} parentName={parentName} />
      </div>
      {menuSections.length === 0 ? <span className="w-8 shrink-0" /> : <GroupMenu name={name} sections={menuSections} disabled={busy} />}
    </li>
  );
}
